import TruncatedTypography from '@/src/components/common/ui/truncatedTypography'
import { useSrpContext } from '@/src/contexts'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import { Airline } from '@/src/hooks/srp/airlineFilterManager'
import { FilterAirlineOption } from '@/src/types/flight'
import { Box, Checkbox, Tooltip, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

const SEOAirlineFilter = () => {
    const {
        SEOSrpData: { filterData }
    } = useSrpContext()
    const { airlineFilter } = filterData || {}
    const t = useTranslations('srpFilters')
    const title = t('airline')
    const { formatCurrency } = useFormatCurrencyForSrp()
    const airlines: Airline[] | undefined = useMemo(
        () =>
            airlineFilter
                ?.map((option: FilterAirlineOption) => ({
                    id: option.value,
                    name: option.label,
                    logoUrl: `https://ui.cltp.co/images/logos/air-logos/svg_logos/${option.value}.svg`,
                    price: !isNaN(Number(option.priceLabel)) ? formatCurrency(option.priceLabel!) : '',
                    unFormattedPrice: option.priceLabel || 0
                }))
                .sort((a: any, b: any) => {
                    //name can be undefined
                    if (!a.name || !b.name) return 0
                    return a.name.localeCompare(b.name)
                }),
        [airlineFilter?.options, formatCurrency]
    )
    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.900' }}>
                    {title}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Individual airlines */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {airlines?.map(airline => (
                        <Box
                            key={airline.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                position: 'relative',
                                '&:hover .airline-only-text': {
                                    opacity: 1
                                }
                            }}
                        >
                            <Checkbox
                                checked={!!airline.isSelected}
                                sx={{
                                    color: 'grey.400',
                                    '&.Mui-checked': {
                                        color: 'brand.primary'
                                    },
                                    p: 0.5
                                }}
                            />
                            <Box
                                sx={{
                                    ml: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flex: 1
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        component="img"
                                        src={airline.logoUrl}
                                        alt={airline.name}
                                        sx={{
                                            height: 16,
                                            width: 24,
                                            objectFit: 'contain'
                                        }}
                                    />
                                    <Box sx={{ position: 'relative' }}>
                                        <Tooltip title={airline.name} followCursor>
                                            <TruncatedTypography
                                                fontSize={'0.875rem'}
                                                maxWidth={200}
                                                maxLines={1}
                                                variant="body2"
                                                color="grey.700"
                                            >
                                                {airline.name}
                                            </TruncatedTypography>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

export default SEOAirlineFilter
